import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import DirectionsCarOutlinedIcon from '@mui/icons-material/DirectionsCarOutlined';
import CircularProgress from '@mui/material/CircularProgress';
import React, { KeyboardEvent, useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { agent } from 'services/service';
import { EmployeeVehicle } from 'services/VehicleService';
import './index.scss';

interface Props {
  target: 'me' | { employeeInfoId: string };
  emptyText?: string;
  readOnly?: boolean;
}

const getErrorMessage = (err: any, fallback: string): string =>
  err?.response?.data?.message ?? fallback;

export const VehicleListEditor = ({
  target,
  emptyText = 'No vehicles added yet.',
  readOnly = false
}: Props) => {
  const employeeInfoId = target === 'me' ? null : target.employeeInfoId;

  const [vehicles, setVehicles] = useState<EmployeeVehicle[]>([]);
  const [loading, setLoading] = useState(true);
  const [vehicleNumber, setVehicleNumber] = useState('');
  const [adding, setAdding] = useState(false);
  const [removingId, setRemovingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = employeeInfoId
        ? await agent.VehicleService.getEmployeeVehicles(employeeInfoId)
        : await agent.VehicleService.getMyVehicles();
      setVehicles(response.data);
    } catch (err) {
      setVehicles([]);
      setError(getErrorMessage(err, 'Could not load vehicles.'));
    } finally {
      setLoading(false);
    }
  }, [employeeInfoId]);

  useEffect(() => {
    load();
  }, [load]);

  const handleAdd = async () => {
    const trimmed = vehicleNumber.trim();
    if (!trimmed || adding) return;

    setAdding(true);
    setError(null);
    try {
      const response = employeeInfoId
        ? await agent.VehicleService.addEmployeeVehicle(employeeInfoId, trimmed)
        : await agent.VehicleService.addMyVehicle(trimmed);
      setVehicles((current) => [...current, response.data]);
      setVehicleNumber('');
      toast.success(`${response.data.vehicleNumber} added.`);
    } catch (err) {
      setError(getErrorMessage(err, 'Could not add the vehicle.'));
    } finally {
      setAdding(false);
    }
  };

  const handleRemove = async (vehicle: EmployeeVehicle) => {
    if (removingId) return;

    setRemovingId(vehicle.id);
    setError(null);
    try {
      if (employeeInfoId) {
        await agent.VehicleService.deleteEmployeeVehicle(
          employeeInfoId,
          vehicle.id
        );
      } else {
        await agent.VehicleService.deleteMyVehicle(vehicle.id);
      }
      setVehicles((current) => current.filter((v) => v.id !== vehicle.id));
      toast.success(`${vehicle.vehicleNumber} removed.`);
    } catch (err) {
      setError(getErrorMessage(err, 'Could not remove the vehicle.'));
    } finally {
      setRemovingId(null);
    }
  };

  return (
    <div
      className={`vehicle-editor${readOnly ? ' vehicle-editor--read-only' : ''}`}
    >
      {!readOnly && (
        <div className="vehicle-editor__add">
          <div
            className={`vehicle-editor__field${
              error ? ' vehicle-editor__field--error' : ''
            }`}
          >
            <DirectionsCarOutlinedIcon fontSize="small" aria-hidden />
            <input
              type="text"
              value={vehicleNumber}
              maxLength={20}
              placeholder="e.g. WP CAB-1234"
              aria-label="Vehicle number"
              aria-invalid={!!error}
              disabled={adding}
              onChange={(event) => {
                setVehicleNumber(event.target.value);
                setError(null);
              }}
              onKeyDown={(event: KeyboardEvent<HTMLInputElement>) => {
                if (event.key === 'Enter') {
                  event.preventDefault();
                  handleAdd();
                }
              }}
            />
          </div>
          <button
            type="button"
            className="vehicle-editor__add-btn"
            disabled={adding || !vehicleNumber.trim()}
            onClick={handleAdd}
          >
            {adding ? (
              <CircularProgress size={14} color="inherit" aria-hidden />
            ) : (
              <AddIcon fontSize="small" aria-hidden />
            )}
            <span>Add vehicle</span>
          </button>
        </div>
      )}

      {error ? (
        <p className="vehicle-editor__error" role="alert">
          {error}
        </p>
      ) : (
        !readOnly && (
          <p className="vehicle-editor__hint">
            Letters, numbers, spaces and dashes. Spacing and case don't matter.
          </p>
        )
      )}

      {loading ? (
        <div className="vehicle-editor__loading">
          <CircularProgress size={18} />
          <span>Loading vehicles...</span>
        </div>
      ) : vehicles.length > 0 ? (
        <div className="vehicle-editor__saved">
          <span className="vehicle-editor__count">
            {vehicles.length} vehicle{vehicles.length === 1 ? '' : 's'}
          </span>
          <ul className="vehicle-editor__list">
            {vehicles.map((vehicle) => (
              <li key={vehicle.id} className="vehicle-editor__plate">
                <span className="vehicle-editor__number">
                  {vehicle.vehicleNumber}
                </span>
                {!readOnly && (
                  <button
                    type="button"
                    className="vehicle-editor__remove-btn"
                    aria-label={`Remove ${vehicle.vehicleNumber}`}
                    title="Remove"
                    disabled={removingId !== null}
                    onClick={() => handleRemove(vehicle)}
                  >
                    {removingId === vehicle.id ? (
                      <CircularProgress size={14} color="inherit" aria-hidden />
                    ) : (
                      <CloseIcon fontSize="small" aria-hidden />
                    )}
                  </button>
                )}
              </li>
            ))}
          </ul>
        </div>
      ) : (
        <div className="vehicle-editor__empty">
          <DirectionsCarOutlinedIcon aria-hidden />
          <span>{emptyText}</span>
        </div>
      )}
    </div>
  );
};
